"use client";

import React, { useMemo } from "react";

import { useIsLoggedIn } from "@dynamic-labs/sdk-react-core";

import { isActive } from "@/utils/links";
import { SupLogo, SupText } from "@repo/core-ui";
import Link from "next/link";
import { usePathname } from "next/navigation";

import AccountSwitchEnv from "@/components/Ui/Account/SwitchEnv";
import Web3MyAccount from "@/components/Web3/Account";
import LayoutMenuAccount from "@/components/Layout/Menu/Account";
import LayoutMenuWallet from "@/components/Layout/Menu/Wallet";
import ThemeToggle from "@/components/Layout/ThemeToggle";

import { useTheme } from "@/hooks/useTheme";
import dynamic from "next/dynamic";
import { twMerge } from "tailwind-merge";

const LayoutMenuCart = dynamic(() => import("@/components/Layout/Menu/Cart"), { ssr: false, loading: () => <div className="sup-filter-bar" /> });

const dAppNavigation = [
  {
    name: "Earn",
    href: "/explore/",
    current: false,
    strict: true,
    extraConditions: [
      { href: "/explore", strict: false },
      { href: "/vaults", strict: false },
      { href: "/piggy", strict: false },
      { href: "/vault", strict: false },
      { href: "/protocol", strict: false },
    ],
  },
  {
    name: "Portfolio",
    href: "/portfolio/",
    current: false,
    strict: false,
    extraConditions: [
      { href: "/portfolio", strict: false },
      { href: "/activity", strict: false },
    ],
  },
  {
    name: "Rewards",
    href: "/rewards/",
    current: false,
    strict: false,
    useATag: true,
    extraConditions: [
      { href: "/season2", strict: false },
      { href: "/season1", strict: false },
    ],
  },
];

export default function SupLayoutHeader() {
  const pathname = usePathname();
  const isConnected = useIsLoggedIn();
  const { theme, toggleTheme } = useTheme();

  const navLinks = useMemo(
    () =>
      dAppNavigation.map(item => ({
        ...item,
        current: isActive(pathname, item.extraConditions || item.href, item.strict),
      })),
    [pathname]
  );

  return (
    <header className="fixed top-0 z-20 hidden h-14 w-full items-center justify-between border-b-[0.5px] border-[rgba(137,121,217,0.2)] pr-4 md:flex">
      <div className="flex h-full items-stretch">
        <Link className="flex items-center justify-start px-4 py-2" href="/">
          <SupLogo svgProps={{ className: "w-auto h-5" }} />
        </Link>
        {navLinks.map(item =>
          item.useATag ? (
            <a
              key={item.name}
              href={item.href}
              className={twMerge(
                "group flex flex-nowrap items-center space-x-2 px-4 text-neutral-900 hover:shadow-[0_0_8px_0_primary-50]",
                item.current && "text-primary-900 shadow-[0_0_8px_0_primary-50]"
              )}
            >
              <SupText
                variant="subtitle1"
                weight={item.current ? "bold" : "regular"}
                className="duration-100 group-hover:font-bold group-hover:text-primary-900 group-hover:ease-in"
              >
                {item.name}
              </SupText>
            </a>
          ) : (
            <Link
              key={item.name}
              href={item.href}
              className={twMerge(
                "group flex flex-nowrap items-center space-x-2 px-4 text-neutral-900 hover:shadow-[0_0_8px_0_primary-50]",
                item.current && "text-primary-900 shadow-[0_0_8px_0_primary-50]"
              )}
            >
              <SupText
                variant="subtitle1"
                weight={item.current ? "bold" : "regular"}
                className="duration-100 group-hover:font-bold group-hover:text-primary-900 group-hover:ease-in"
              >
                {item.name}
              </SupText>
            </Link>
          )
        )}
      </div>
      <div className="min-w-screen ml-auto flex w-full items-center justify-end space-x-4 md:w-fit md:justify-between lg:ml-4 lg:min-w-fit">
        <AccountSwitchEnv />
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
        {isConnected && <LayoutMenuAccount />}
        {isConnected && <LayoutMenuWallet />}
        <LayoutMenuCart />
        {!isConnected && <Web3MyAccount />}
      </div>
    </header>
  );
}
